import { useEffect, useRef, useState } from 'react';
import { mat4, quat, vec2, vec3, vec4 } from 'gl-matrix';
import './InfiniteGlassMenu.css';

const discVertShaderSource = `#version 300 es

uniform mat4 uWorldMatrix;
uniform mat4 uViewMatrix;
uniform mat4 uProjectionMatrix;
uniform vec3 uCameraPosition;
uniform vec4 uRotationAxisVelocity;

in vec3 aModelPosition;
in vec3 aModelNormal;
in vec2 aModelUvs;
in mat4 aInstanceMatrix;

out vec2 vUvs;
out float vAlpha;
flat out int vInstanceId;

#define PI 3.141593

void main() {
    vec4 worldPosition = uWorldMatrix * aInstanceMatrix * vec4(aModelPosition, 1.);

    vec3 centerPos = (uWorldMatrix * aInstanceMatrix * vec4(0., 0., 0., 1.)).xyz;
    float radius = length(centerPos.xyz);

    if (gl_VertexID > 0) {
        vec3 rotationAxis = uRotationAxisVelocity.xyz;
        float rotationVelocity = min(.15, uRotationAxisVelocity.w * 15.);
        vec3 stretchDir = normalize(cross(centerPos, rotationAxis));
        vec3 relativeVertexPos = normalize(worldPosition.xyz - centerPos);
        float strength = dot(stretchDir, relativeVertexPos);
        float invAbsStrength = min(0., abs(strength) - 1.);
        strength = rotationVelocity * sign(strength) * abs(invAbsStrength * invAbsStrength * invAbsStrength + 1.);
        worldPosition.xyz += stretchDir * strength;
    }

    worldPosition.xyz = radius * normalize(worldPosition.xyz);

    gl_Position = uProjectionMatrix * uViewMatrix * worldPosition;

    vAlpha = smoothstep(0.5, 1., normalize(worldPosition.xyz).z) * .9 + .1;
    vUvs = aModelUvs;
    vInstanceId = gl_InstanceID;
}
`;

const discFragShaderSource = `#version 300 es
precision highp float;

uniform vec4 uIconColors[10];
uniform int uItemCount;
uniform float uTime;
uniform sampler2D uEmojiTexture;
uniform sampler2D uIconTexture0;
uniform sampler2D uIconTexture1;
uniform sampler2D uIconTexture2;
uniform sampler2D uIconTexture3;
uniform sampler2D uIconTexture4;
uniform sampler2D uIconTexture5;
uniform sampler2D uIconTexture6;
uniform sampler2D uIconTexture7;
uniform sampler2D uIconTexture8;
uniform sampler2D uIconTexture9;
uniform bool uHasIconTextures;

out vec4 outColor;

in vec2 vUvs;
in float vAlpha;
flat in int vInstanceId;

// Função para criar efeito Liquid Glass da Apple - transparente mas opaco
vec3 createAppleLiquidGlassEffect(vec2 uv, vec3 baseColor) {
    // Distância do centro
    float dist = length(uv - 0.5);
    
    // Gradiente radial suave para transparência controlada
    float radialGradient = smoothstep(0.0, 0.5, dist);
    
    // Reflexos animados sutis como no Liquid Glass da Apple
    vec2 reflection1 = vec2(sin(uTime * 0.2 + uv.x * 3.0), cos(uTime * 0.15 + uv.y * 2.5));
    vec2 reflection2 = vec2(cos(uTime * 0.25 + uv.y * 3.5), sin(uTime * 0.2 + uv.x * 2.8));
    
    // Reflexos mais pronunciados mas elegantes
    float reflectionStrength1 = 0.15 * (1.0 - dist) * (0.3 + 0.7 * reflection1.x);
    float reflectionStrength2 = 0.12 * (1.0 - dist) * (0.3 + 0.7 * reflection2.y);
    
    // Cores de reflexo brancas e elegantes
    vec3 reflectionColor1 = vec3(0.98, 0.99, 1.0) * reflectionStrength1;
    vec3 reflectionColor2 = vec3(0.96, 0.98, 1.0) * reflectionStrength2;
    
    // Base mais transparente - cor base mais diluída
    vec3 dilutedBaseColor = baseColor * 0.15; // Mais transparente
    
    // EFEITO DE DEGRADÊ NAS BORDAS - estilo Apple
    vec2 center = uv - 0.5;
    
    // Gradiente vertical - mais branco na parte inferior (estilo Apple)
    float verticalGradient = smoothstep(-0.5, 0.5, center.y);
    
    // Gradiente horizontal - mais branco nas laterais
    float horizontalGradient = 1.0 - smoothstep(0.0, 0.3, abs(center.x));
    
    // Gradiente circular - mais branco nas bordas externas
    float edgeGradient = smoothstep(0.2, 0.4, dist);
    
    // Combinar gradientes para efeito Apple
    float glassGradient = (verticalGradient * 0.5 + horizontalGradient * 0.3 + edgeGradient * 0.2);
    
    // Cor branca do vidro com intensidade mais sutil
    vec3 glassWhite = vec3(0.95, 0.98, 1.0) * glassGradient * 0.3;
    
    // Misturar para efeito Liquid Glass da Apple
    vec3 glassColor = dilutedBaseColor * (1.0 - radialGradient * 0.2) + reflectionColor1 + reflectionColor2 + glassWhite;
    
    return glassColor;
}

// Função para criar área do emoji no centro (círculo maior)
float createEmojiArea(vec2 uv) {
    vec2 center = uv - 0.5;
    float dist = length(center);
    
    // Área ajustada para o ícone (centro do círculo) - bordas mais precisas
    float emojiArea = smoothstep(0.65, 0.15, dist); // Área mais precisa para evitar artefatos
    
    return emojiArea;
}

void main() {
    int itemIndex = vInstanceId % uItemCount;
    
    // Obter cor do ícone baseada no índice
    vec3 iconColor = uIconColors[itemIndex].rgb;
    
    // Criar efeito Liquid Glass da Apple
    vec3 glassColor = createAppleLiquidGlassEffect(vUvs, iconColor);
    
    // Criar borda suave do círculo
    float dist = length(vUvs - 0.5);
    float circleEdge = smoothstep(0.45, 0.5, dist);
    
    // Área central para ícone
    float centerArea = createEmojiArea(vUvs);
    
    // Cor final - efeito Liquid Glass da Apple
    vec3 finalColor = glassColor;
    
    // Se há texturas de ícones, aplicar o ícone específico na área central
    if (uHasIconTextures && centerArea > 0.0 && itemIndex < 10) {
        // Selecionar textura baseada no índice do item - corrigindo inversão Y e diminuindo tamanho
        vec2 flippedUvs = vec2(vUvs.x, 1.0 - vUvs.y);
        // Diminuir tamanho do ícone centralizando as coordenadas UV
        vec2 centeredUvs = (flippedUvs - 0.5) * 1.25 + 0.5;
        
        // Verificar se as coordenadas UV estão dentro dos limites válidos
        bool validUVs = all(greaterThanEqual(centeredUvs, vec2(0.0))) && all(lessThanEqual(centeredUvs, vec2(1.0)));
        
        vec4 iconColor = vec4(0.0);
        if (validUVs) {
            // Só ler a textura se as coordenadas UV forem válidas
            if (itemIndex == 0) iconColor = texture(uIconTexture0, centeredUvs);
            else if (itemIndex == 1) iconColor = texture(uIconTexture1, centeredUvs);
            else if (itemIndex == 2) iconColor = texture(uIconTexture2, centeredUvs);
            else if (itemIndex == 3) iconColor = texture(uIconTexture3, centeredUvs);
            else if (itemIndex == 4) iconColor = texture(uIconTexture4, centeredUvs);
            else if (itemIndex == 5) iconColor = texture(uIconTexture5, centeredUvs);
            else if (itemIndex == 6) iconColor = texture(uIconTexture6, centeredUvs);
            else if (itemIndex == 7) iconColor = texture(uIconTexture7, centeredUvs);
            else if (itemIndex == 8) iconColor = texture(uIconTexture8, centeredUvs);
            else if (itemIndex == 9) iconColor = texture(uIconTexture9, centeredUvs);
        }
        
        // Se o ícone tem conteúdo, usar ele diretamente na área central
        if (iconColor.a > 0.5) {
            // Usar apenas pixels com alpha suficiente para evitar artefatos de borda
            finalColor = mix(glassColor, iconColor.rgb, centerArea * iconColor.a);
        }
    }
    
    // Transparência ajustada para efeito Apple - mais transparente
    float glassAlpha = (1.0 - circleEdge) * vAlpha * 0.4; 
    
    // Alpha final para transparência controlada
    float finalAlpha = glassAlpha;
    
    outColor = vec4(finalColor, finalAlpha);
}
`;

class Face {
  constructor(a, b, c) {
    this.a = a;
    this.b = b;
    this.c = c;
  }
}

class Vertex {
  constructor(x, y, z) {
    this.position = vec3.fromValues(x, y, z);
    this.normal = vec3.create();
    this.uv = vec2.create();
  }
}

class Geometry {
  constructor() {
    this.vertices = [];
    this.faces = [];
  }

  addVertex(...args) {
    for (let i = 0; i < args.length; i += 3) {
      this.vertices.push(new Vertex(args[i], args[i + 1], args[i + 2]));
    }
    return this;
  }

  addFace(...args) {
    for (let i = 0; i < args.length; i += 3) {
      this.faces.push(new Face(args[i], args[i + 1], args[i + 2]));
    }
    return this;
  }

  get lastVertex() {
    return this.vertices[this.vertices.length - 1];
  }

  subdivide(divisions = 1) {
    const midPointCache = {};
    let f = this.faces;

    for (let div = 0; div < divisions; ++div) {
      const newFaces = new Array(f.length * 4);

      f.forEach((face, ndx) => {
        const mAB = this.getMidPoint(face.a, face.b, midPointCache);
        const mBC = this.getMidPoint(face.b, face.c, midPointCache);
        const mCA = this.getMidPoint(face.c, face.a, midPointCache);

        const i = ndx * 4;
        newFaces[i + 0] = new Face(face.a, mAB, mCA);
        newFaces[i + 1] = new Face(face.b, mBC, mAB);
        newFaces[i + 2] = new Face(face.c, mCA, mBC);
        newFaces[i + 3] = new Face(mAB, mBC, mCA);
      });

      f = newFaces;
    }

    this.faces = f;
    return this;
  }

  spherize(radius = 1) {
    this.vertices.forEach(vertex => {
      vec3.normalize(vertex.normal, vertex.position);
      vec3.scale(vertex.position, vertex.normal, radius);
    });
    return this;
  }

  get data() {
    return {
      vertices: this.vertexData,
      indices: this.indexData,
      normals: this.normalData,
      uvs: this.uvData
    };
  }

  get vertexData() {
    return new Float32Array(this.vertices.flatMap(v => Array.from(v.position)));
  }

  get normalData() {
    return new Float32Array(this.vertices.flatMap(v => Array.from(v.normal)));
  }

  get uvData() {
    return new Float32Array(this.vertices.flatMap(v => Array.from(v.uv)));
  }

  get indexData() {
    return new Uint16Array(this.faces.flatMap(f => [f.a, f.b, f.c]));
  }

  getMidPoint(ndxA, ndxB, cache) {
    const cacheKey = ndxA < ndxB ? `k_${ndxB}_${ndxA}` : `k_${ndxA}_${ndxB}`;
    if (Object.prototype.hasOwnProperty.call(cache, cacheKey)) {
      return cache[cacheKey];
    }
    const a = this.vertices[ndxA].position;
    const b = this.vertices[ndxB].position;
    const ndx = this.vertices.length;
    cache[cacheKey] = ndx;
    this.addVertex((a[0] + b[0]) * 0.5, (a[1] + b[1]) * 0.5, (a[2] + b[2]) * 0.5);
    return ndx;
  }
}

class IcosahedronGeometry extends Geometry {
  constructor() {
    super();
    const t = Math.sqrt(5) * 0.5 + 0.5;
    this.addVertex(
      -1,
      t,
      0,
      1,
      t,
      0,
      -1,
      -t,
      0,
      1,
      -t,
      0,
      0,
      -1,
      t,
      0,
      1,
      t,
      0,
      -1,
      -t,
      0,
      1,
      -t,
      t,
      0,
      -1,
      t,
      0,
      1,
      -t,
      0,
      -1,
      -t,
      0,
      1
    ).addFace(
      0,
      11,
      5,
      0,
      5,
      1,
      0,
      1,
      7,
      0,
      7,
      10,
      0,
      10,
      11,
      1,
      5,
      9,
      5,
      11,
      4,
      11,
      10,
      2,
      10,
      7,
      6,
      7,
      1,
      8,
      3,
      9,
      4,
      3,
      4,
      2,
      3,
      2,
      6,
      3,
      6,
      8,
      3,
      8,
      9,
      4,
      9,
      5,
      2,
      4,
      11,
      6,
      2,
      10,
      8,
      6,
      7,
      9,
      8,
      1
    );
  }
}

class DiscGeometry extends Geometry {
  constructor(steps = 4, radius = 1) {
    super();
    steps = Math.max(4, steps);

    const alpha = (2 * Math.PI) / steps;

    this.addVertex(0, 0, 0);
    this.lastVertex.uv[0] = 0.5;
    this.lastVertex.uv[1] = 0.5;

    for (let i = 0; i < steps; ++i) {
      const x = Math.cos(alpha * i);
      const y = Math.sin(alpha * i);
      this.addVertex(radius * x, radius * y, 0);
      this.lastVertex.uv[0] = x * 0.5 + 0.5;
      this.lastVertex.uv[1] = y * 0.5 + 0.5;

      if (i > 0) {
        this.addFace(0, i, i + 1);
      }
    }
    this.addFace(0, steps, 1);
  }
}

function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);

  if (success) {
    return shader;
  }

  console.error(gl.getShaderInfoLog(shader));
  gl.deleteShader(shader);
  return null;
}

function createProgram(gl, shaderSources, transformFeedbackVaryings, attribLocations) {
  const program = gl.createProgram();
  if (!program) {
    console.error('Falha ao criar programa WebGL');
    return null;
  }

  const shaders = [];
  [gl.VERTEX_SHADER, gl.FRAGMENT_SHADER].forEach((type, ndx) => {
    const shader = createShader(gl, type, shaderSources[ndx]);
    if (shader) {
      gl.attachShader(program, shader);
      shaders.push(shader);
    } else {
      console.error(`Falha ao criar shader ${ndx === 0 ? 'vertex' : 'fragment'}`);
      gl.deleteProgram(program);
      return null;
    }
  });

  if (transformFeedbackVaryings) {
    gl.transformFeedbackVaryings(program, transformFeedbackVaryings, gl.SEPARATE_ATTRIBS);
  }

  if (attribLocations) {
    for (const attrib in attribLocations) {
      gl.bindAttribLocation(program, attribLocations[attrib], attrib);
    }
  }

  gl.linkProgram(program);
  const success = gl.getProgramParameter(program, gl.LINK_STATUS);

  if (success) {
    // Limpar shaders após linkagem bem-sucedida
    shaders.forEach(shader => gl.deleteShader(shader));
    return program;
  }

  console.error('Erro ao linkar programa:', gl.getProgramInfoLog(program));
  shaders.forEach(shader => gl.deleteShader(shader));
  gl.deleteProgram(program);
  return null;
}

function makeVertexArray(gl, bufLocNumElmPairs, indices) {
  const va = gl.createVertexArray();
  gl.bindVertexArray(va);

  for (const [buffer, loc, numElem] of bufLocNumElmPairs) {
    if (loc === -1) continue;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, numElem, gl.FLOAT, false, 0, 0);
  }

  if (indices) {
    const indexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);
  }

  gl.bindVertexArray(null);
  return va;
}

function resizeCanvasToDisplaySize(canvas) {
  const dpr = Math.min(2, window.devicePixelRatio);
  const displayWidth = Math.round(canvas.clientWidth * dpr);
  const displayHeight = Math.round(canvas.clientHeight * dpr);
  const needResize = canvas.width !== displayWidth || canvas.height !== displayHeight;
  if (needResize) {
    canvas.width = displayWidth;
    canvas.height = displayHeight;
  }
  return needResize;
}

function makeBuffer(gl, sizeOrData, usage) {
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, sizeOrData, usage);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return buf;
}

class ArcballControl {
  isPointerDown = false;
  orientation = quat.create();
  pointerRotation = quat.create();
  rotationVelocity = 0;
  rotationAxis = vec3.fromValues(1, 0, 0);
  snapDirection = vec3.fromValues(0, 0, -1);
  snapTargetDirection;
  EPSILON = 0.1;
  IDENTITY_QUAT = quat.create();

  constructor(canvas, updateCallback) {
    this.canvas = canvas;
    this.updateCallback = updateCallback || (() => null);

    this.pointerPos = vec2.create();
    this.previousPointerPos = vec2.create();
    this._rotationVelocity = 0;
    this._combinedQuat = quat.create();

    canvas.addEventListener('pointerdown', e => {
      vec2.set(this.pointerPos, e.clientX, e.clientY);
      vec2.copy(this.previousPointerPos, this.pointerPos);
      this.isPointerDown = true;
    });
    canvas.addEventListener('pointerup', () => {
      this.isPointerDown = false;
    });
    canvas.addEventListener('pointerleave', () => {
      this.isPointerDown = false;
    });
    canvas.addEventListener('pointermove', e => {
      if (this.isPointerDown) {
        vec2.set(this.pointerPos, e.clientX, e.clientY);
      }
    });

    canvas.style.touchAction = 'none';
  }

  update(deltaTime, targetFrameDuration = 16) {
    const timeScale = deltaTime / targetFrameDuration + 0.00001;
    let angleFactor = timeScale;
    let snapRotation = quat.create();

    if (this.isPointerDown) {
      const INTENSITY = 0.3 * timeScale;
      const ANGLE_AMPLIFICATION = 5 / timeScale;

      const midPointerPos = vec2.sub(vec2.create(), this.pointerPos, this.previousPointerPos);
      vec2.scale(midPointerPos, midPointerPos, INTENSITY);

      if (vec2.sqrLen(midPointerPos) > this.EPSILON) {
        vec2.add(midPointerPos, this.previousPointerPos, midPointerPos);

        const p = this.#project(midPointerPos);
        const q = this.#project(this.previousPointerPos);
        const a = vec3.normalize(vec3.create(), p);
        const b = vec3.normalize(vec3.create(), q);

        vec2.copy(this.previousPointerPos, midPointerPos);

        angleFactor *= ANGLE_AMPLIFICATION;

        this.quatFromVectors(a, b, this.pointerRotation, angleFactor);
      } else {
        quat.slerp(this.pointerRotation, this.pointerRotation, this.IDENTITY_QUAT, INTENSITY);
      }
    } else {
      const INTENSITY = 0.1 * timeScale;
      quat.slerp(this.pointerRotation, this.pointerRotation, this.IDENTITY_QUAT, INTENSITY);

      if (this.snapTargetDirection) {
        const SNAPPING_INTENSITY = 0.2;
        const a = this.snapTargetDirection;
        const b = this.snapDirection;
        const sqrDist = vec3.squaredDistance(a, b);
        const distanceFactor = Math.max(0.1, 1 - sqrDist * 10);
        angleFactor *= SNAPPING_INTENSITY * distanceFactor;
        this.quatFromVectors(a, b, snapRotation, angleFactor);
      }
    }

    const combinedQuat = quat.multiply(quat.create(), snapRotation, this.pointerRotation);
    this.orientation = quat.multiply(quat.create(), combinedQuat, this.orientation);
    quat.normalize(this.orientation, this.orientation);

    const RA_INTENSITY = 0.8 * timeScale;
    quat.slerp(this._combinedQuat, this._combinedQuat, combinedQuat, RA_INTENSITY);
    quat.normalize(this._combinedQuat, this._combinedQuat);

    const rad = Math.acos(this._combinedQuat[3]) * 2.0;
    const s = Math.sin(rad / 2.0);
    let rv = 0;
    if (s > 0.000001) {
      rv = rad / (2 * Math.PI);
      this.rotationAxis[0] = this._combinedQuat[0] / s;
      this.rotationAxis[1] = this._combinedQuat[1] / s;
      this.rotationAxis[2] = this._combinedQuat[2] / s;
    }

    const RV_INTENSITY = 0.5 * timeScale;
    this._rotationVelocity += (rv - this._rotationVelocity) * RV_INTENSITY;
    this.rotationVelocity = this._rotationVelocity / timeScale;

    this.updateCallback(deltaTime);
  }

  quatFromVectors(a, b, out, angleFactor = 1) {
    const axis = vec3.cross(vec3.create(), a, b);
    vec3.normalize(axis, axis);
    const d = Math.max(-1, Math.min(1, vec3.dot(a, b)));
    const angle = Math.acos(d) * angleFactor;
    quat.setAxisAngle(out, axis, angle);
    return { q: out, axis, angle };
  }

  #project(pos) {
    const r = 2;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const s = Math.max(w, h) - 1;

    const x = (2 * pos[0] - w - 1) / s;
    const y = (2 * pos[1] - h - 1) / s;
    let z = 0;
    const xySq = x * x + y * y;
    const rSq = r * r;

    if (xySq <= rSq / 2.0) {
      z = Math.sqrt(rSq - xySq);
    } else {
      z = rSq / Math.sqrt(xySq);
    }
    return vec3.fromValues(-x, y, z);
  }
}

class InfiniteGlassMenu {
  TARGET_FRAME_DURATION = 1000 / 60;
  SPHERE_RADIUS = 2;

  #time = 0;
  #deltaTime = 0;
  #deltaFrames = 0;
  #frames = 0;

  camera = {
    matrix: mat4.create(),
    near: 0.1,
    far: 40,
    fov: Math.PI / 4,
    aspect: 1,
    position: vec3.fromValues(0, 0, 3),
    up: vec3.fromValues(0, 1, 0),
    matrices: {
      view: mat4.create(),
      projection: mat4.create(),
      inversProjection: mat4.create()
    }
  };

  nearestVertexIndex = null;
  smoothRotationVelocity = 0;
  scaleFactor = 1.0;
  movementActive = false;

  constructor(canvas, items, onActiveItemChange, onMovementChange, onInit = null) {
    this.canvas = canvas;
    this.items = items || [];
    this.onActiveItemChange = onActiveItemChange || (() => {});
    this.onMovementChange = onMovementChange || (() => {});
    this.iconTextures = [];
    this.hasIconTextures = false;
    this.activeItemIndex = -1; // Índice do item ativo
    this.#init(onInit);
  }

  resize() {
    this.viewportSize = vec2.set(this.viewportSize || vec2.create(), this.canvas.clientWidth, this.canvas.clientHeight);

    const gl = this.gl;
    const needsResize = resizeCanvasToDisplaySize(gl.canvas);
    if (needsResize) {
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    }

    this.#updateProjectionMatrix(gl);
  }

  run(time = 0) {
    this.#deltaTime = Math.min(32, time - this.#time);
    this.#time = time;
    this.#deltaFrames = this.#deltaTime / this.TARGET_FRAME_DURATION;
    this.#frames += this.#deltaFrames;

    this.#animate(this.#deltaTime);
    this.#render();

    requestAnimationFrame(t => this.run(t));
  }

  #init(onInit) {
    this.gl = this.canvas.getContext('webgl2', { antialias: true, alpha: true });
    const gl = this.gl;
    if (!gl) {
      throw new Error('No WebGL 2 context!');
    }

    this.viewportSize = vec2.fromValues(this.canvas.clientWidth, this.canvas.clientHeight);
    this.drawBufferSize = vec2.clone(this.viewportSize);

    this.discProgram = createProgram(gl, [discVertShaderSource, discFragShaderSource], null, {
      aModelPosition: 0,
      aModelNormal: 1,
      aModelUvs: 2,
      aInstanceMatrix: 3
    });

    if (!this.discProgram) {
      throw new Error('Falha ao criar programa shader');
    }

    this.discLocations = {
      aModelPosition: gl.getAttribLocation(this.discProgram, 'aModelPosition'),
      aModelUvs: gl.getAttribLocation(this.discProgram, 'aModelUvs'),
      aInstanceMatrix: gl.getAttribLocation(this.discProgram, 'aInstanceMatrix'),
      uWorldMatrix: gl.getUniformLocation(this.discProgram, 'uWorldMatrix'),
      uViewMatrix: gl.getUniformLocation(this.discProgram, 'uViewMatrix'),
      uProjectionMatrix: gl.getUniformLocation(this.discProgram, 'uProjectionMatrix'),
      uCameraPosition: gl.getUniformLocation(this.discProgram, 'uCameraPosition'),
      uScaleFactor: gl.getUniformLocation(this.discProgram, 'uScaleFactor'),
      uRotationAxisVelocity: gl.getUniformLocation(this.discProgram, 'uRotationAxisVelocity'),
      uIconColors: gl.getUniformLocation(this.discProgram, 'uIconColors'),
      uItemCount: gl.getUniformLocation(this.discProgram, 'uItemCount'),
      uTime: gl.getUniformLocation(this.discProgram, 'uTime'),
      uIconTexture0: gl.getUniformLocation(this.discProgram, 'uIconTexture0'),
      uIconTexture1: gl.getUniformLocation(this.discProgram, 'uIconTexture1'),
      uIconTexture2: gl.getUniformLocation(this.discProgram, 'uIconTexture2'),
      uIconTexture3: gl.getUniformLocation(this.discProgram, 'uIconTexture3'),
      uIconTexture4: gl.getUniformLocation(this.discProgram, 'uIconTexture4'),
      uIconTexture5: gl.getUniformLocation(this.discProgram, 'uIconTexture5'),
      uIconTexture6: gl.getUniformLocation(this.discProgram, 'uIconTexture6'),
      uIconTexture7: gl.getUniformLocation(this.discProgram, 'uIconTexture7'),
      uIconTexture8: gl.getUniformLocation(this.discProgram, 'uIconTexture8'),
      uIconTexture9: gl.getUniformLocation(this.discProgram, 'uIconTexture9'),
      uHasIconTextures: gl.getUniformLocation(this.discProgram, 'uHasIconTextures')
    };

    this.discGeo = new DiscGeometry(56, 1);
    this.discBuffers = this.discGeo.data;
    this.discVAO = makeVertexArray(
      gl,
      [
        [makeBuffer(gl, this.discBuffers.vertices, gl.STATIC_DRAW), this.discLocations.aModelPosition, 3],
        [makeBuffer(gl, this.discBuffers.uvs, gl.STATIC_DRAW), this.discLocations.aModelUvs, 2]
      ],
      this.discBuffers.indices
    );

    this.icoGeo = new IcosahedronGeometry();
    this.icoGeo.subdivide(1).spherize(this.SPHERE_RADIUS);
    this.instancePositions = this.icoGeo.vertices.map(v => v.position);
    this.DISC_INSTANCE_COUNT = this.icoGeo.vertices.length;
    this.#initDiscInstances(this.DISC_INSTANCE_COUNT);

    this.worldMatrix = mat4.create();
    this.#initIconColors();
    this.#loadIconTextures();

    this.control = new ArcballControl(this.canvas, deltaTime => this.#onControlUpdate(deltaTime));

    this.#updateCameraMatrix();
    this.#updateProjectionMatrix(gl);
    this.resize();

    if (onInit) onInit(this);
  }

  #initIconColors() {
    // Cores elegantes estilo Apple Liquid Glass - tons brancos e sutis
    this.iconColors = [
      [0.95, 0.97, 1.0, 1.0],  // azul muito claro - estilo Apple
      [0.97, 0.95, 1.0, 1.0],  // roxo muito claro - estilo Apple
      [1.0, 0.95, 0.97, 1.0],  // rosa muito claro - estilo Apple
      [0.96, 0.96, 1.0, 1.0],  // índigo muito claro - estilo Apple
      [1.0, 0.97, 0.95, 1.0],  // laranja muito claro - estilo Apple
      [0.95, 1.0, 0.97, 1.0],  // verde muito claro - estilo Apple
      [1.0, 0.95, 1.0, 1.0],   // magenta muito claro - estilo Apple
      [0.95, 1.0, 1.0, 1.0],   // ciano muito claro - estilo Apple
      [1.0, 1.0, 0.95, 1.0],   // amarelo muito claro - estilo Apple
      [0.98, 0.98, 0.98, 1.0]  // cinza muito claro - estilo Apple
    ];
  }

  #loadIconTextures() {
    if (!this.items || this.items.length === 0) return;

    const gl = this.gl;
    this.iconTextures = new Array(Math.min(this.items.length, 10));
    let loadedCount = 0;

    this.items.slice(0, 10).forEach((item, index) => {
      if (!item.icon) {
        this.iconTextures[index] = null;
        loadedCount++;
        if (loadedCount === this.items.length) {
          this.hasIconTextures = true;
        }
        return;
      }

      // Verificar se é uma URL de imagem ou emoji
      const isImageUrl = item.icon.startsWith('http') || item.icon.includes('.webp') || item.icon.startsWith('data:') || item.icon.includes('.png') || item.icon.includes('.jpg') || item.icon.includes('.svg');

      if (isImageUrl) {
        // Carregar imagem da URL
        const image = new Image();
        image.crossOrigin = 'anonymous';
        
        image.onload = () => {
          const texture = gl.createTexture();
          gl.bindTexture(gl.TEXTURE_2D, texture);
          
          // Configurar parâmetros da textura
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          
          // Carregar imagem na textura
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
          
          this.iconTextures[index] = texture;
          gl.bindTexture(gl.TEXTURE_2D, null);

          console.log(`Imagem ${index} (${item.icon}) carregada com sucesso`);

          loadedCount++;
          if (loadedCount === this.items.length) {
            this.hasIconTextures = true;
            console.log(`Total de ${loadedCount} ícones carregados`);
          }
        };
        
        image.onerror = () => {
          console.warn('Erro ao carregar imagem:', item.icon);
          this.iconTextures[index] = null;
          loadedCount++;
          if (loadedCount === this.items.length) {
            this.hasIconTextures = true;
          }
        };
        
        image.src = item.icon;
      } else {
        // Renderizar emoji no canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 256;
        canvas.height = 256;

        // Limpar canvas com fundo transparente
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Configurar contexto para renderização de alta qualidade
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = 'bold 50px Arial, sans-serif';
        ctx.fillStyle = 'white';

        // Renderizar emoji no canvas
        ctx.fillText(item.icon, canvas.width / 2, canvas.height / 2);

        // Criar textura WebGL
        const texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, texture);
        
        // Configurar parâmetros da textura
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        
        // Carregar canvas na textura
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
        
        this.iconTextures[index] = texture;
        gl.bindTexture(gl.TEXTURE_2D, null);

        console.log(`Emoji ${index} (${item.icon}) carregado com sucesso`);

        loadedCount++;
        if (loadedCount === this.items.length) {
          this.hasIconTextures = true;
          console.log(`Total de ${loadedCount} ícones carregados`);
        }
      }
    });
  }

  #initDiscInstances(count) {
    const gl = this.gl;
    this.discInstances = {
      matricesArray: new Float32Array(count * 16),
      matrices: [],
      buffer: gl.createBuffer()
    };
    for (let i = 0; i < count; ++i) {
      const instanceMatrixArray = new Float32Array(this.discInstances.matricesArray.buffer, i * 16 * 4, 16);
      instanceMatrixArray.set(mat4.create());
      this.discInstances.matrices.push(instanceMatrixArray);
    }
    gl.bindVertexArray(this.discVAO);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.discInstances.matricesArray.byteLength, gl.DYNAMIC_DRAW);
    const mat4AttribSlotCount = 4;
    const bytesPerMatrix = 16 * 4;
    for (let j = 0; j < mat4AttribSlotCount; ++j) {
      const loc = this.discLocations.aInstanceMatrix + j;
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 4, gl.FLOAT, false, bytesPerMatrix, j * 4 * 4);
      gl.vertexAttribDivisor(loc, 1);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);
  }

  #animate(deltaTime) {
    const gl = this.gl;
    this.control.update(deltaTime, this.TARGET_FRAME_DURATION);

    let positions = this.instancePositions.map(p => vec3.transformQuat(vec3.create(), p, this.control.orientation));
    const scale = 0.25;
    const SCALE_INTENSITY = 0.6;
    positions.forEach((p, ndx) => {
      const s = (Math.abs(p[2]) / this.SPHERE_RADIUS) * SCALE_INTENSITY + (1 - SCALE_INTENSITY);
      const finalScale = s * scale;
      const matrix = mat4.create();
      mat4.multiply(matrix, matrix, mat4.fromTranslation(mat4.create(), vec3.negate(vec3.create(), p)));
      mat4.multiply(matrix, matrix, mat4.targetTo(mat4.create(), [0, 0, 0], p, [0, 1, 0]));
      mat4.multiply(matrix, matrix, mat4.fromScaling(mat4.create(), [finalScale, finalScale, finalScale]));
      mat4.multiply(matrix, matrix, mat4.fromTranslation(mat4.create(), [0, 0, -this.SPHERE_RADIUS]));

      mat4.copy(this.discInstances.matrices[ndx], matrix);
    });

    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.buffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.discInstances.matricesArray);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    this.smoothRotationVelocity = this.control.rotationVelocity;
  }

  #render() {
    const gl = this.gl;
    gl.useProgram(this.discProgram);

    gl.enable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);

    gl.clearColor(0, 0, 0, 0.0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.uniformMatrix4fv(this.discLocations.uWorldMatrix, false, this.worldMatrix);
    gl.uniformMatrix4fv(this.discLocations.uViewMatrix, false, this.camera.matrices.view);
    gl.uniformMatrix4fv(this.discLocations.uProjectionMatrix, false, this.camera.matrices.projection);
    gl.uniform3f(
      this.discLocations.uCameraPosition,
      this.camera.position[0],
      this.camera.position[1],
      this.camera.position[2]
    );
    gl.uniform4f(
      this.discLocations.uRotationAxisVelocity,
      this.control.rotationAxis[0],
      this.control.rotationAxis[1],
      this.control.rotationAxis[2],
      this.smoothRotationVelocity * 1.1
    );

    gl.uniform1i(this.discLocations.uItemCount, this.items.length);
    gl.uniform4fv(this.discLocations.uIconColors, this.iconColors.flat());
    gl.uniform1f(this.discLocations.uTime, this.#time * 0.001);
    gl.uniform1i(this.discLocations.uHasIconTextures, this.hasIconTextures ? 1 : 0);

    // Bind das texturas de ícones se disponíveis
    if (this.hasIconTextures && this.iconTextures) {
      const textureLocations = [
        this.discLocations.uIconTexture0,
        this.discLocations.uIconTexture1,
        this.discLocations.uIconTexture2,
        this.discLocations.uIconTexture3,
        this.discLocations.uIconTexture4,
        this.discLocations.uIconTexture5,
        this.discLocations.uIconTexture6,
        this.discLocations.uIconTexture7,
        this.discLocations.uIconTexture8,
        this.discLocations.uIconTexture9
      ];

      this.iconTextures.forEach((texture, index) => {
        if (texture && textureLocations[index]) {
          gl.activeTexture(gl.TEXTURE1 + index);
          gl.bindTexture(gl.TEXTURE_2D, texture);
          gl.uniform1i(textureLocations[index], 1 + index);
        }
      });
      
      // Log para debug (apenas na primeira renderização)
      if (this.#frames < 2) {
        console.log(`Renderizando com ${this.iconTextures.filter(t => t).length} texturas de ícones`);
      }
    }

    gl.bindVertexArray(this.discVAO);
    gl.drawElementsInstanced(
      gl.TRIANGLES,
      this.discBuffers.indices.length,
      gl.UNSIGNED_SHORT,
      0,
      this.DISC_INSTANCE_COUNT
    );
  }

  #updateCameraMatrix() {
    mat4.targetTo(this.camera.matrix, this.camera.position, [0, 0, 0], this.camera.up);
    mat4.invert(this.camera.matrices.view, this.camera.matrix);
  }

  #updateProjectionMatrix(gl) {
    this.camera.aspect = gl.canvas.clientWidth / gl.canvas.clientHeight;
    const height = this.SPHERE_RADIUS * 0.35;
    const distance = this.camera.position[2];
    if (this.camera.aspect > 1) {
      this.camera.fov = 2 * Math.atan(height / distance);
    } else {
      this.camera.fov = 2 * Math.atan(height / this.camera.aspect / distance);
    }
    mat4.perspective(
      this.camera.matrices.projection,
      this.camera.fov,
      this.camera.aspect,
      this.camera.near,
      this.camera.far
    );
    mat4.invert(this.camera.matrices.inversProjection, this.camera.matrices.projection);
  }

  #onControlUpdate(deltaTime) {
    const timeScale = deltaTime / this.TARGET_FRAME_DURATION + 0.0001;
    let damping = 5 / timeScale;
    let cameraTargetZ = 3;

    const isMoving = this.control.isPointerDown || Math.abs(this.smoothRotationVelocity) > 0.01;

    if (isMoving !== this.movementActive) {
      this.movementActive = isMoving;
      this.onMovementChange(isMoving);
    }

    // Sempre atualizar o item ativo, independente do estado do ponteiro
    const nearestVertexIndex = this.#findNearestVertexIndex();
    const itemIndex = nearestVertexIndex % Math.max(1, this.items.length);
    
    // Só atualizar se o item mudou para evitar chamadas desnecessárias
    if (this.activeItemIndex !== itemIndex) {
      this.activeItemIndex = itemIndex;
      this.onActiveItemChange(itemIndex);
    }

    if (!this.control.isPointerDown) {
      const snapDirection = vec3.normalize(vec3.create(), this.#getVertexWorldPosition(nearestVertexIndex));
      this.control.snapTargetDirection = snapDirection;
    } else {
      cameraTargetZ += this.control.rotationVelocity * 80 + 2.5;
      damping = 7 / timeScale;
    }

    this.camera.position[2] += (cameraTargetZ - this.camera.position[2]) / damping;
    this.#updateCameraMatrix();
  }

  #findNearestVertexIndex() {
    const n = this.control.snapDirection;
    const inversOrientation = quat.conjugate(quat.create(), this.control.orientation);
    const nt = vec3.transformQuat(vec3.create(), n, inversOrientation);

    let maxD = -1;
    let nearestVertexIndex;
    for (let i = 0; i < this.instancePositions.length; ++i) {
      const d = vec3.dot(nt, this.instancePositions[i]);
      if (d > maxD) {
        maxD = d;
        nearestVertexIndex = i;
      }
    }
    return nearestVertexIndex;
  }

  #getVertexWorldPosition(index) {
    const nearestVertexPos = this.instancePositions[index];
    return vec3.transformQuat(vec3.create(), nearestVertexPos, this.control.orientation);
  }

}

const defaultItems = [
  {
    icon: '🎬',
    link: 'https://www.netflix.com/',
    title: 'Filmes',
    description: 'Explore nossa coleção de filmes'
  },
  {
    icon: '🎵',
    link: 'https://open.spotify.com/',
    title: 'Música',
    description: 'Descubra novas músicas'
  },
  {
    icon: '📚',
    link: 'https://www.goodreads.com/',
    title: 'Livros',
    description: 'Biblioteca digital completa'
  },
  {
    icon: '🎮',
    link: 'https://store.steampowered.com/',
    title: 'Jogos',
    description: 'Entretenimento interativo'
  }
];

export default function InfiniteGlassMenuComponent({ items = [], enableShadow = false }) {
  const canvasRef = useRef(null);
  const sketchRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  const [mainItemActive, setMainItemActive] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    let sketch;

    const handleActiveItem = index => {
      const itemIndex = index % items.length;
      const currentItem = items[itemIndex];
      console.log('Active item:', currentItem);
      setActiveItem(currentItem);
      
      // Verificar se o item ativo tem isMainIcon = true
      const isMain = currentItem && currentItem.isMainIcon === true;
      setMainItemActive(isMain);
    };

    if (canvas) {
      sketch = new InfiniteGlassMenu(canvas, items.length ? items : defaultItems, handleActiveItem, setIsMoving, sk => {
        sketchRef.current = sk;
        sk.run();
      });
    }

    const handleResize = () => {
      if (sketch) {
        sketch.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [items]);

  const handleActionClick = () => {
    if (activeItem && activeItem.link) {
      window.location.replace(activeItem.link);
    }
  };

  return (

    <div style={{ position: 'relative', width: '100%', height: '100%' }} className={enableShadow ? 'infinite-glass-menu-with-shadow' : ''}>
      <canvas id="infinite-glass-menu-canvas" ref={canvasRef} />
      
      {/* Logo Principal - Texto no canto inferior direito com fontes responsivas */}
      {mainItemActive && !isMoving && activeItem && (
        <div
          style={{
            position: 'fixed',
            right: '3%',
            bottom: '7%',
            maxWidth: 'min(90ch, 60vw)',
            opacity: 1,
            transition: 'opacity 0.5s ease',
            zIndex: 1000,
            pointerEvents: 'none',
            textAlign: 'right',
          }}
          className="main-logo-text main-logo-text-bottom-right"
        >
          <div className="main-logo-text-content">
            <h2
              className="main-logo-title"
              style={{ fontSize: 'clamp(1.25rem, 2.2vw, 2rem)', margin: 0 }}
            >
              {activeItem.title}
            </h2>
            <h3
              className="main-logo-title"
              style={{ fontSize: 'clamp(1rem, 1.8vw, 1.5rem)', fontWeight: 200, margin: 0 }}
            >
              {activeItem.description}
            </h3>
          </div>
        </div>
      )}
      
      {/* Botão de ação que aparece quando há um item ativo */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '81%',
          transform: 'translateX(-50%)',
          opacity: (activeItem && !isMoving) ? 1 : 0,
          transition: 'opacity 0.3s ease, transform 0.3s ease',
          pointerEvents: (activeItem && !isMoving) ? 'auto' : 'none',
        }}
      >
        <button
          onClick={handleActionClick}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#8B5CF6', 
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            color: 'white',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
            transition: 'all 0.3s ease',
            zIndex: 1000,
            transform: 'scale(1)',
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
          }}
        >
          <i className="fa fa-arrow-right"></i>
        </button>
      </div>
    </div>
  );
}
